import React, { useRef, useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Component, ChartConfig } from "@/types";
import {
  getChartSizeConfig,
  getChartResponsiveConfig,
  getBreakpointChartConfig,
  ResponsiveChartConfigInput,
} from "@/utils/chart-responsive-utils";
import { useQueryTemplateData, isQueryTemplateDataSource, getXAxisKeyFromQuery, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { humanizeFieldName } from "@/utils";
import { formatAxisTick } from "@/utils/PropertyFormatters";
import { ChartDataSource, ComputedField } from "@/types/chartDataSource";
import { enrichDataWithComputedFields } from "@/utils/computedFields";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartHoverStore } from "@/stores/chart/chart-hover-store";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { useComponentData } from "@/stores/editor/component-data-store";
import { normalizeChartRows } from "./chartDataUtils";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface BarChartProps {
  component: Component;
  isPreview?: boolean;
}

const defaultData = [
  { name: "Jan", value: 400 },
  { name: "Feb", value: 300 },
  { name: "Mar", value: 500 },
  { name: "Apr", value: 280 },
  { name: "May", value: 590 },
  { name: "Jun", value: 320 },
];

const defaultColors = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
];

type ChartRow = Record<string, unknown>;

type ChartMouseMoveState = {
  activePayload?: Array<{ payload?: ChartRow }>;
  activeTooltipIndex?: number | string | null;
};

type BarClickPayload = {
  payload?: ChartRow;
  index?: number | string;
};

type LegendFormatterValue = {
  name?: unknown;
  key?: unknown;
};

function toOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

const isBarClickPayload = (value: unknown): value is BarClickPayload => {
  return typeof value === "object" && value !== null;
};

const formatLegendValue = (value: unknown): React.ReactNode => {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (typeof value === "object" && value !== null) {
    const legendValue = value as LegendFormatterValue;
    const displayValue = legendValue.name ?? legendValue.key;
    if (typeof displayValue === "string" || typeof displayValue === "number") {
      return displayValue;
    }
  }

  return String(value);
};

export const BarChart: React.FC<BarChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Phase 2: Track container width for responsive breakpoint detection
  const [containerWidth, setContainerWidth] = useState(400);

  // Get dataSource configuration
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
    // Don't seed with static template data — it's placeholder-scale and causes wrong Y-axis
    // until live data arrives. Charts show empty during loading instead.
  });

  // Component data store (populated by fetch-schema-data interactions)
  const { data: storeData, loading: storeLoading, error: storeError } = useComponentData(component.id);

  // Loading/error state: merge store + query template
  const loading = storeLoading || qtLoading;
  const error = storeError || qtError;

  // Data priority: Query template > Store data > Static data > Default data
  // When a live query is configured but still loading, use [] rather than stale static data —
  // static data in the template often has placeholder values at a completely different scale.
  const hasLiveQuery = hasQueryTemplate || !!templateId;
  const chartData = useMemo(() => {
    const hasLiveQueryTemplateData = hasLiveQuery && Array.isArray(queryTemplateData);

    if (hasLiveQueryTemplateData) {
      return queryTemplateData;
    }

    if (storeData.length > 0) {
      return storeData;
    }

    if (hasLiveQuery) {
      return queryTemplateData || [];
    }

    return properties.data && Array.isArray(properties.data) ? properties.data : defaultData;
  }, [hasLiveQuery, properties.data, queryTemplateData, storeData]);

  const normalizedChartData = useMemo(
    () => normalizeChartRows(chartData as ChartRow[]),
    [chartData]
  );

  // Enrich data with computed fields (derived columns like "profit = revenue - cost")
  const enrichedData = useMemo(
    () => enrichDataWithComputedFields(normalizedChartData, properties.computedFields as ComputedField[] | undefined),
    [normalizedChartData, properties.computedFields]
  );

  // Write enriched data to chart hover store for linked DataText idle display.
  // Also publish under _templateNodeId for backwards-compat with old placements.
  const setChartData = useChartHoverStore((state) => state.setChartData);
  const templateNodeId = properties._templateNodeId as string | undefined;
  useEffect(() => {
    if (enrichedData && enrichedData.length > 0) {
      setChartData(component.id, enrichedData);
      if (templateNodeId) setChartData(templateNodeId, enrichedData);
    }
  }, [enrichedData, component.id, templateNodeId, setChartData]);

  // Get size configuration from actual rendered width (via ResizeObserver) for accurate responsive behavior
  const sizeConfig = getChartSizeConfig(containerWidth, toOptionalNumber(size?.height));
  const { isSmall } = sizeConfig;

  // Get responsive configuration
  const title = properties.title as string | undefined;
  const description = properties.description as string;
  const userMargin = properties.margin as ChartConfig['margin'];

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    userMargin,
    properties.showLegend !== false,
    properties.showGrid !== false
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [resolvedColors, setResolvedColors] = useState<string[]>(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Phase 2: Track container width for responsive breakpoint detection
  // IMPORTANT: Use offsetWidth instead of getBoundingClientRect().width
  // because the editor canvas applies CSS transforms (scale) which affects
  // getBoundingClientRect but not offsetWidth. We want the actual layout
  // size, not the visually scaled size, for breakpoint detection.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      // Use offsetWidth for transform-independent measurement
      const width = container.offsetWidth;
      setContainerWidth(width);
    };

    // Initial measurement
    updateWidth();

    // Watch for size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Phase 2: Get breakpoint-specific configuration
  const responsiveChartConfig = properties.responsiveChartConfig as ResponsiveChartConfigInput | undefined;
  const breakpointConfig = getBreakpointChartConfig(containerWidth, responsiveChartConfig);

  // Phase 2: Apply breakpoint overrides to properties
  const effectiveProperties: Record<string, unknown> = breakpointConfig ? {
    ...properties,
    ...(breakpointConfig.showLegend !== undefined && { showLegend: breakpointConfig.showLegend }),
    ...(breakpointConfig.showGrid !== undefined && { showGrid: breakpointConfig.showGrid }),
    ...(breakpointConfig.showTooltip !== undefined && { showTooltip: breakpointConfig.showTooltip }),
    ...(breakpointConfig.showXAxis !== undefined && { showXAxis: breakpointConfig.showXAxis }),
    ...(breakpointConfig.showYAxis !== undefined && { showYAxis: breakpointConfig.showYAxis }),
    ...(breakpointConfig.margin !== undefined && { margin: breakpointConfig.margin }),
  } : properties;

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible
  // Re-resolve when palette changes to update CSS variable values
  useLayoutEffect(() => {
    const colors = (effectiveProperties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [effectiveProperties.colors, paletteChangeCounter]);

  // Determine axis keys - use query definition if available, otherwise use properties
  const queryXKey = hasQueryTemplate && dataSource?.queryDefinition
    ? getXAxisKeyFromQuery(dataSource.queryDefinition) : null;
  const autoXKey = normalizedChartData.length > 0
    ? Object.keys(normalizedChartData[0]).find(k => typeof normalizedChartData[0][k] === 'string' || k.includes('date') || k.includes('time'))
    : null;
  // Only use the explicit xAxisKey if it actually exists in the data — otherwise fall through to
  // queryXKey / autoXKey so stale template field names don't prevent auto-detection.
  const explicitXKey = properties.xAxisKey as string | undefined;
  const explicitXKeyValid = explicitXKey && (normalizedChartData.length === 0 || explicitXKey in normalizedChartData[0]);
  const xAxisKey = (explicitXKeyValid ? explicitXKey : null)
    || (queryXKey && normalizedChartData.length > 0 && queryXKey in normalizedChartData[0] ? queryXKey : null)
    || autoXKey
    || queryXKey
    || "name";

  // Get bar keys - support barKeys, lineKeys (from backend), query definition (validated), or auto-detect
  const queryBarKeys = hasQueryTemplate && dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition) : null;
  const validatedBarKeys = queryBarKeys && normalizedChartData.length > 0
    ? queryBarKeys.filter(k => k in normalizedChartData[0])
    : queryBarKeys;
  // Validate explicit keys against actual data — fall through if they don't match
  const explicitBarKeys = Array.isArray(properties.barKeys)
    ? properties.barKeys.filter((key): key is string => typeof key === 'string')
    : Array.isArray(properties.lineKeys)
      ? properties.lineKeys.filter((key): key is string => typeof key === 'string')
      : null;
  const validatedExplicitBarKeys = explicitBarKeys && normalizedChartData.length > 0
    ? explicitBarKeys.filter(k => k in normalizedChartData[0])
    : explicitBarKeys;
  const detectedBarKeys = (validatedExplicitBarKeys && validatedExplicitBarKeys.length > 0 ? validatedExplicitBarKeys : null)
    || (validatedBarKeys && validatedBarKeys.length > 0 ? validatedBarKeys : null)
    || (Array.isArray(properties.datasets)
      ? properties.datasets.flatMap((dataset) =>
          typeof dataset === 'object' &&
          dataset !== null &&
          'key' in dataset &&
          typeof dataset.key === 'string'
            ? [dataset.key]
            : []
        )
      : null)
    || (normalizedChartData.length > 0
        ? Object.keys(normalizedChartData[0]).filter(key => key !== xAxisKey && typeof normalizedChartData[0][key] === 'number')
        : null)
    || ["value"];

  // Inject computed field names into keys (don't rely on auto-detection — null values fail typeof check)
  const computedFieldNames = (properties.computedFields as ComputedField[] | undefined)?.map(f => f.name) || [];
  const barKeys = [
    ...detectedBarKeys,
    ...computedFieldNames.filter(name => !detectedBarKeys.includes(name)),
  ];

  // Dynamic margin adjustment: when Y-axis is hidden, set right margin to 0 for edge-to-edge
  // When Y-axis is showing, use default right margin for visual balance
  const baseMargin = responsiveConfig.margin || { top: 10, right: 20, bottom: 20, left: 20 };
  const adjustedMargin = {
    ...baseMargin,
    right: effectiveProperties.showYAxis === false ? 0 : (baseMargin.right ?? 20),
  };

  const chartConfig: ChartConfig = {
    title,
    data: enrichedData,
    xAxisKey,
    yAxisKey: (properties.yAxisKey as string) || "value",
    showLegend: responsiveConfig.showLegend,
    showTooltip: properties.showTooltip !== false,
    showGrid: responsiveConfig.showGrid,
    colors: resolvedColors,
    responsive: properties.responsive !== false,
    animation: properties.animation !== false,
    margin: adjustedMargin,
  };

  // Get dataset names for legend labels (if using datasets property)
  const datasets = properties.datasets as Array<{key: string; color?: string; name?: string}> | undefined;
  const getBarName = (key: string): string => {
    if (datasets) {
      const dataset = datasets.find(d => d.key === key);
      if (dataset?.name) return dataset.name;
    }
    // Humanize the field name for better readability (e.g., "water_depth" → "Water Depth")
    return humanizeFieldName(key);
  };

  // Chart hover store handlers for compound chart integration
  const setHoverData = useChartHoverStore((state) => state.setHoverData);
  const clearHoverData = useChartHoverStore((state) => state.clearHoverData);

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  const handleChartMouseMove = useCallback(
    (e: ChartMouseMoveState | undefined) => {
      if (e?.activePayload?.[0]?.payload) {
        const payload = e.activePayload[0].payload;
        const index = toOptionalNumber(e.activeTooltipIndex) ?? null;
        setHoverData(component.id, payload, index);
        if (templateNodeId) setHoverData(templateNodeId, payload, index);
      }
    },
    [component.id, templateNodeId, setHoverData]
  );

  const handleChartMouseLeave = useCallback(() => {
    clearHoverData(component.id);
    if (templateNodeId) clearHoverData(templateNodeId);
  }, [component.id, templateNodeId, clearHoverData]);

  const handleBarClick = useCallback(
    (key: string) => (data: unknown) => {
      const payload = isBarClickPayload(data) ? data.payload ?? {} : {};
      const index = isBarClickPayload(data) ? toOptionalNumber(data.index) : undefined;
      void handleChartClick(payload, key, index);
    },
    [handleChartClick]
  );

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 50 : sizeConfig.isSmall ? 80 : 100;

  // Container style - let it fill the wrapper
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: (typeof properties.minHeight === 'string' || typeof properties.minHeight === 'number')
      ? properties.minHeight
      : `${dynamicMinimum + (responsiveConfig.showTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {responsiveConfig.showTitle && properties.showInternalTitle !== false && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{chartConfig.title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="bar" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : normalizedChartData && normalizedChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              key={`${component.id}-${barKeys.join(',')}`}
              data={chartConfig.data}
              margin={chartConfig.margin}
              style={{ userSelect: isPreview ? "auto" : "none" }}
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
            >
            {chartConfig.showGrid && effectiveProperties.showXAxis !== false && effectiveProperties.showYAxis !== false && (
              <CartesianGrid strokeDasharray="3 3" />
            )}
            <XAxis
              dataKey={chartConfig.xAxisKey}
              tick={effectiveProperties.showXAxis === false ? false : { fontSize: responsiveConfig.axisFontSize }}
              tickFormatter={(value) => formatAxisTick(value)}
              axisLine={effectiveProperties.showXAxis !== false}
              tickLine={effectiveProperties.showXAxis !== false}
              padding={{ left: 10, right: 10 }}
              label={effectiveProperties.xAxisLabel && effectiveProperties.showXAxis !== false && !isSmall ? { value: String(effectiveProperties.xAxisLabel), position: 'insideBottom', offset: -10, fontSize: responsiveConfig.axisFontSize } : undefined}
            />
            <YAxis
              width={responsiveConfig.yAxisWidth}
              tick={effectiveProperties.showYAxis === false ? false : { fontSize: responsiveConfig.axisFontSize }}
              axisLine={effectiveProperties.showYAxis !== false}
              tickLine={effectiveProperties.showYAxis !== false}
              label={effectiveProperties.yAxisLabel && effectiveProperties.showYAxis !== false && !isSmall ? { value: String(effectiveProperties.yAxisLabel), angle: -90, position: 'insideLeft', fontSize: responsiveConfig.axisFontSize } : undefined}
            />
            {chartConfig.showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "6px",
                }}
                content={effectiveProperties.showTooltipContent === false ? () => null : undefined}
                cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1 }}
              />
            )}
            {chartConfig.showLegend && (
              <Legend
                wrapperStyle={{
                  fontSize: `${responsiveConfig.legendFontSize}px`,
                  paddingTop: '35px'
                }}
                formatter={formatLegendValue}
              />
            )}
            {/* Support multiple bar keys for grouped/stacked charts */}
            {barKeys.map((key: string, index: number) => {
              const baseColor = chartConfig.colors?.[index] || defaultColors[index % defaultColors.length];
              const useConditionalColors = properties.useConditionalColors as boolean;
              const positiveColor = (properties.positiveColor as string) || '#10B981';
              const negativeColor = (properties.negativeColor as string) || '#EF4444';

              // Phase 1.1: Bar size
              const barSize = typeof properties.barSize === 'number' ? properties.barSize : undefined;

              // Phase 1.2: Stroke properties
              const strokeWidth = typeof properties.barStrokeWidth === 'number' ? properties.barStrokeWidth : undefined;
              const stroke = strokeWidth && strokeWidth > 0 ? (properties.barStroke as string) || '#333333' : undefined;
              const strokeDasharray = properties.barStrokeDasharray as string | undefined;

              // Phase 1.3: Opacity
              const fillOpacity = typeof properties.barOpacity === 'number' ? properties.barOpacity : undefined;

              // Phase 1.4: Corner radius
              const cornerRadius = typeof properties.cornerRadius === 'number'
                ? [properties.cornerRadius, properties.cornerRadius, 0, 0] as [number, number, number, number]
                : [2, 2, 0, 0] as [number, number, number, number];

              // Determine if this series has navigation configured
              const seriesNavigation = getNavigationForElement(key);
              const hasSeriesNavigation = isNavigable && seriesNavigation;

              return (
                <Bar
                  key={key}
                  dataKey={key}
                  name={getBarName(key)}
                  fill={useConditionalColors ? undefined : baseColor}
                  stackId={properties.stacked ? "stack" : undefined}
                  animationDuration={chartConfig.animation ? 300 : 0}
                  radius={cornerRadius}
                  barSize={barSize}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  strokeDasharray={strokeDasharray}
                  fillOpacity={fillOpacity}
                  onClick={handleBarClick(key)}
                  cursor={hasSeriesNavigation ? 'pointer' : 'default'}
                >
                  {/* Phase 1.4: Conditional coloring for positive/negative values */}
                  {useConditionalColors && chartConfig.data?.map((entry: ChartRow, entryIndex: number) => {
                    const value = entry[key];
                    const cellColor = typeof value === 'number' && value < 0 ? negativeColor : positiveColor;
                    const xAxisKey = (properties.xAxisKey as string) || 'name';
                    const cellNavigation = getNavigationForElement(key, String(entry[xAxisKey] ?? ''));
                    return (
                      <Cell
                        key={`cell-${entryIndex}`}
                        fill={cellColor}
                        cursor={isNavigable && cellNavigation ? 'pointer' : 'default'}
                      />
                    );
                  })}
                </Bar>
              );
            })}
          </RechartsBarChart>
        </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
